/**
 * usePaymentHandlers hook
 *
 * Encapsulates all payment-related event handlers with API client integration.
 */

import {
  useState,
  useRef,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react';
import type {
  VoiceDTMFSessionState,
  SavedCard,
  CardEntryState,
  MockDTMFScenario,
} from '@/types/voice-dtmf.types';
import type { AgentApiClient } from '@services/agent/AgentApiClient';
import { ApiError, type PaymentDeclinedResponse } from '@/types/api.types';
import {
  DEFAULT_CARDHOLDER_NAME,
  UX_DELAYS,
  MOCK_DELAYS,
  ROUTES,
} from '@utils/constants';
import { logger } from '@utils/logger';
import { appendQueryParams } from '@utils/appendQueryParams';
import { handleSessionTermination } from '@utils/sessionTermination';

interface UsePaymentHandlersProps {
  sessionState: VoiceDTMFSessionState;
  setSessionState: Dispatch<SetStateAction<VoiceDTMFSessionState>>;
  sessionId: string;
  paymentId?: string;
  amount: number;
  currency: string;
  useMockData?: boolean;
  mockScenario?: string;
  onSessionDeclined?: (paymentId?: string) => void;
  onFallbackToManual?: (sessionId: string) => void;
  onError?: (error: Error) => void;
  refetch: (scenarioOverride?: MockDTMFScenario) => void;
  apiClient?: AgentApiClient | null;
  interactionType?: 'VOICE' | 'VOICE_MANUAL' | 'CHAT';
  paymentLink?: string;
  initialSession?: PaymentSession;
  checkoutOpenedRef?: MutableRefObject<boolean>;
  checkoutWindowRef?: MutableRefObject<Window | null>;
  shouldSaveCard?: 'ALWAYS' | 'OPTIONAL' | 'NEVER';
}

/**
 * Create empty CardEntry state
 *
 * Factory function to initialize CardEntry with default values.
 * Prevents duplication of empty state initialization across handlers.
 */
function createEmptyCardEntry(
  shouldSaveCard: 'ALWAYS' | 'OPTIONAL' | 'NEVER' = 'NEVER'
): CardEntryState {
  return {
    cardholderName: '',
    cardNumber: {
      value: '',
      isComplete: false,
      digitsEntered: 0,
      totalDigits: 16,
    },
    cvv: { value: '', isComplete: false, digitsEntered: 0, totalDigits: 3 },
    expiryDate: '',
    saveCard: shouldSaveCard === 'ALWAYS',
  };
}

/**
 * Create empty card number field state
 */
function createEmptyCardNumber() {
  return { value: '', isComplete: false, digitsEntered: 0, totalDigits: 16 };
}

/**
 * Create empty CVV field state
 */
function createEmptyCVV() {
  return { value: '', isComplete: false, digitsEntered: 0, totalDigits: 3 };
}

export const usePaymentHandlers = ({
  sessionState,
  setSessionState,
  sessionId,
  paymentId,
  amount,
  currency,
  useMockData = false,
  mockScenario = 'new-card-success',
  onSessionDeclined,
  onFallbackToManual,
  onError,
  refetch,
  apiClient,
  interactionType,
  paymentLink,
  initialSession,
  checkoutOpenedRef,
  checkoutWindowRef,
  shouldSaveCard = 'NEVER',
}: UsePaymentHandlersProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  // Suppresses the PAN/CVV validation UI while a cancel is in flight, so a late
  // SSE FIELD_INVALID/FIELD_PROGRESS event cannot flash an error on the fields
  // before the iframe navigates away (PAS-32798; originally PAS-32055, lost in a
  // rebase and re-applied here — QA-reported cancel flash).
  const [isCancelling, setIsCancelling] = useState(false);
  // Synchronous re-entrancy guard for payment submission. A ref (not state) so a
  // second click in the same tick is blocked before React re-renders and disables
  // the button — prevents duplicate /submit calls (PAS-32663).
  const isSubmittingRef = useRef(false);

  const handlePaymentMethodSelect = async (
    method: 'saved-card' | 'new-card',
    cardId?: string
  ) => {
    // When switching to new-card in mock mode, restart with the new-card flow
    // so DTMF simulation for card number and CVV runs from scratch.
    // Exception: partner-mode has its own flow (OFF→ON transition) — don't override it.
    if (
      method === 'new-card' &&
      useMockData &&
      mockScenario !== 'partner-mode' &&
      mockScenario !== 'partner-mode-demo'
    ) {
      refetch('new-card-success-no-saved-cards');
      return;
    }

    // In mock mode, saved card selection shows processing briefly then goes to success
    if (method === 'saved-card' && cardId && useMockData) {
      const selectedCard = sessionState.savedCards?.find(
        (c) => c.cardId === cardId
      );
      setIsProcessing(true);
      setTimeout(() => {
        setIsProcessing(false);
        setSessionState((prev) => ({
          ...prev,
          selectedPaymentMethod: method,
          selectedSavedCardId: cardId,
          screen: 'success',
          orderInfo: {
            btPayId: `BTPAY-${Math.random().toString(36).substring(7).toUpperCase()}`,
            merchantTransactionId: `ORD-${Date.now()}`,
            amount,
            currency,
            maskedCardNumber:
              selectedCard?.maskedCardNumber ?? '**** **** **** ****',
            cardType: selectedCard?.cardType ?? 'UNKNOWN',
            expiryDate: selectedCard?.expiryDate ?? '',
            timestamp: new Date().toISOString(),
          },
        }));
      }, MOCK_DELAYS.PAYMENT_PROCESSING);
      return;
    }

    // Real mode + saved card: submit token payment immediately
    if (method === 'saved-card' && cardId && !useMockData && apiClient) {
      const selectedCard = sessionState.savedCards?.find(
        (c) => c.cardId === cardId
      );
      setIsProcessing(true);
      setSessionState((prev) => ({
        ...prev,
        selectedPaymentMethod: method,
        selectedSavedCardId: cardId,
        screen: 'processing',
      }));

      try {
        const result = await apiClient.submitTokenPayment(cardId);
        setIsProcessing(false);
        setSessionState((prev) => ({
          ...prev,
          screen: 'success',
          paymentStatus: 'completed',
          orderInfo: {
            btPayId: result.paymentId || sessionId,
            merchantTransactionId:
              initialSession?.merchantTransactionId ||
              result.transactionId ||
              undefined,
            amount,
            currency,
            maskedCardNumber: selectedCard?.maskedCardNumber ?? '****',
            cardType: selectedCard?.cardType ?? 'UNKNOWN',
            expiryDate: selectedCard?.expiryDate ?? '',
            timestamp: new Date().toISOString(),
          },
        }));
      } catch (err) {
        setIsProcessing(false);
        if (
          err instanceof ApiError &&
          err.errorCode?.toUpperCase() === 'MAX_ATTEMPTS_EXCEEDED'
        ) {
          setSessionState((prev) => ({
            ...prev,
            screen: 'error',
            errorType: 'max-attempts',
          }));
          onSessionDeclined?.(
            initialSession?.paymentId ?? paymentId ?? sessionId
          );
        } else if (
          err instanceof ApiError &&
          err.errorCode === 'PAYMENT_DECLINED'
        ) {
          const declined = err.details as PaymentDeclinedResponse | undefined;
          if (declined?.canRetry) {
            setSessionState((prev) => ({
              ...prev,
              screen: 'error',
              errorType: 'dont-retry',
            }));
          } else {
            setSessionState((prev) => ({
              ...prev,
              screen: 'error',
              errorType: 'max-attempts',
            }));
            onSessionDeclined?.(
              initialSession?.paymentId ?? paymentId ?? sessionId
            );
          }
        } else {
          setSessionState((prev) => ({
            ...prev,
            screen: 'error',
            errorType: 'retry-later',
            errorMessage:
              err instanceof Error ? err.message : 'Payment submission failed',
          }));
          onError?.(
            err instanceof Error ? err : new Error('Payment submission failed')
          );
        }
      }
      return;
    }

    let cardEntry: CardEntryState = createEmptyCardEntry(shouldSaveCard);

    if (method === 'saved-card' && cardId) {
      const selectedCard = sessionState.savedCards?.find(
        (c) => c.cardId === cardId
      );
      if (selectedCard) {
        cardEntry = {
          cardholderName: DEFAULT_CARDHOLDER_NAME,
          cardNumber: {
            value: selectedCard.maskedCardNumber,
            isComplete: true,
            digitsEntered: 16,
            totalDigits: 16,
          },
          cvv: {
            value: '',
            isComplete: false,
            digitsEntered: 0,
            totalDigits: undefined,
          },
          expiryDate: selectedCard.expiryDate,
          cardType: selectedCard.cardType,
          saveCard: false,
        };
      }
    }

    setSessionState((prev) => ({
      ...prev,
      selectedPaymentMethod: method,
      selectedSavedCardId: cardId,
      screen: 'card-entry',
      cardEntry,
    }));
  };

  const handleDeleteCardClick = (card: SavedCard) => {
    setSessionState((prev) => ({
      ...prev,
      showDeleteModal: true,
      cardToDeleteId: card.cardId,
    }));
  };

  const handleDeleteCardConfirm = async () => {
    const cardToDelete = sessionState.savedCards?.find(
      (c) => c.cardId === sessionState.cardToDeleteId
    );
    if (!cardToDelete) return;

    setSessionState((prev) => ({
      ...prev,
      showDeleteModal: false,
      cardToDeleteId: undefined,
    }));

    setSessionState((prev) => ({
      ...prev,
      screen: 'deleting-card',
      deletingCard: {
        cardId: cardToDelete.cardId,
        cardName: cardToDelete.cardName,
      },
    }));

    try {
      // Start timer for minimum loading screen display (UX polish)
      const startTime = Date.now();

      // Mock mode - simulate deletion
      if (useMockData) {
        // No actual API call in mock mode, just wait
      } else {
        // Real mode - call API
        if (!apiClient) {
          throw new Error('API client not initialized');
        }

        // DELETE /api/payment/card-tokens/{cardTokenId}
        await apiClient.deleteSavedCard(cardToDelete.cardId);
      }

      // Wait minimum loading time for UX polish (prevents flash for fast operations)
      const elapsed = Date.now() - startTime;
      const remainingDelay = Math.max(
        0,
        UX_DELAYS.MINIMUM_LOADING_SCREEN - elapsed
      );
      if (remainingDelay > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingDelay));
      }

      logger.info('Card deleted successfully', { cardId: cardToDelete.cardId });

      setSessionState((prev) => {
        const updatedCards =
          prev.savedCards?.filter((c) => c.cardId !== cardToDelete.cardId) ||
          [];

        // If no cards left, go to card-entry instead of payment-method-selection.
        // Initialise a fresh cardEntry — without it, handleFieldChange is a no-op
        // (it only mutates when prev.cardEntry exists) and the agent cannot type the
        // cardholder name or expiry date after deleting the last saved card (PAS-32752).
        if (updatedCards.length === 0) {
          return {
            ...prev,
            screen: 'card-entry',
            selectedPaymentMethod: 'new-card',
            selectedSavedCardId: undefined,
            savedCards: [],
            cardEntry: createEmptyCardEntry(shouldSaveCard),
            deletingCard: undefined,
          };
        }

        // Return to payment method selection
        return {
          ...prev,
          screen: 'payment-method-selection',
          savedCards: updatedCards,
          deletingCard: undefined,
        };
      });
    } catch (err) {
      logger.error('Failed to delete card', err);

      const isMandateError =
        err instanceof ApiError && err.errorCode === 'not_supported';

      setSessionState((prev) => ({
        ...prev,
        screen: 'error',
        errorType: isMandateError ? 'card-has-mandates' : 'unexpected',
        errorMessage: isMandateError
          ? undefined
          : err instanceof Error
            ? err.message
            : 'Failed to delete card',
        deletingCard: undefined,
      }));

      onError?.(
        err instanceof Error ? err : new Error('Failed to delete card')
      );
    }
  };

  const handleDeleteCardCancel = () => {
    setSessionState((prev) => ({
      ...prev,
      showDeleteModal: false,
      cardToDeleteId: undefined,
    }));
  };

  const handleResetSensitiveData = async () => {
    logger.info('Resetting sensitive data', { paymentId });

    if (!useMockData && apiClient) {
      try {
        await apiClient.resetSensitiveData();
      } catch (err) {
        logger.error('Reset sensitive data failed', err);
      }
    }

    setSessionState((prev) => ({
      ...prev,
      cardEntry: prev.cardEntry
        ? {
            ...prev.cardEntry,
            cardNumber: createEmptyCardNumber(),
            cvv: createEmptyCVV(),
            cardType: undefined,
          }
        : prev.cardEntry,
    }));
  };

  const handleCompletePayment = async () => {
    // Re-entrancy guard: ignore a second invocation while a submission is in
    // flight. Uses a ref so it takes effect synchronously, before the button's
    // disabled state re-renders — prevents duplicate /submit calls (PAS-32663).
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsProcessing(true);

    if (useMockData) {
      const { generateMockOrderInfo } =
        await import('../../../mocks/mockVoiceDTMFData');
      const { MOCK_DELAYS } = await import('../../../utils/constants');

      setTimeout(() => {
        setIsProcessing(false);
        isSubmittingRef.current = false;

        // Test playground logic - check test cards
        if (mockScenario === 'test-playground') {
          const cardNumber = sessionState.cardEntry?.cardNumber?.value || '';
          const cvvObj = sessionState.cardEntry?.cvv as
            | { value: string; isComplete: boolean; _actualValue?: string }
            | undefined;
          const actualCVV = cvvObj?._actualValue || '';
          const cardEntryObj = sessionState.cardEntry as
            | (CardEntryState & { _actualExpiry?: string })
            | undefined;
          const actualExpiry =
            cardEntryObj?._actualExpiry ||
            sessionState.cardEntry?.expiryDate ||
            '';

          const cleanCardNumber = cardNumber.replace(/[*\s]/g, '');
          let errorType:
            | 'retry-later'
            | 'dont-retry'
            | 'revalidate-input'
            | 'unexpected'
            | null = null;

          // Test cards
          if (cleanCardNumber.endsWith('0002'))
            errorType = 'dont-retry'; // Declined
          else if (cleanCardNumber.endsWith('0069'))
            errorType = 'dont-retry'; // Expired
          else if (cleanCardNumber.endsWith('0127') || actualCVV === '999')
            errorType = 'revalidate-input'; // Mismatch
          else if (cleanCardNumber.endsWith('0101'))
            errorType = 'retry-later'; // DTMF error
          else if (cleanCardNumber.endsWith('0259'))
            errorType = 'unexpected'; // BFF error
          else if (cardEntryObj?._actualExpiry && actualExpiry.includes('/')) {
            const [month, year] = actualExpiry.split('/');
            if (month && year) {
              const fullYear =
                year.length === 2 ? 2000 + parseInt(year) : parseInt(year);
              const expiry = new Date(fullYear, parseInt(month) - 1);
              if (expiry < new Date()) errorType = 'dont-retry';
            }
          }

          if (errorType) {
            const currentAttempts = (sessionState.attemptCount || 0) + 1;
            if (currentAttempts >= 3) {
              setSessionState((prev) => ({
                ...prev,
                screen: 'error',
                errorType: 'max-attempts',
                attemptCount: currentAttempts,
              }));
              handleSessionTermination({
                agentReturnUrl: initialSession?.agentReturnUrl,
                paymentId: initialSession?.paymentId ?? paymentId ?? sessionId,
                status: 'MAX_ATTEMPTS_REACHED',
                sessionId,
              });
              return;
            }
            setSessionState((prev) => ({
              ...prev,
              screen: 'error',
              errorType,
              attemptCount: currentAttempts,
            }));
            return;
          }

          // Success
          const cardData = {
            maskedCardNumber:
              sessionState.cardEntry?.cardNumber?.value ||
              '4111 11** **** 4567',
            cardType: sessionState.cardEntry?.cardType || 'VISA',
            expiryDate: sessionState.cardEntry?.expiryDate || '09/26',
          };
          setSessionState((prev) => ({
            ...prev,
            screen: 'success',
            orderInfo: generateMockOrderInfo(amount, currency, cardData),
          }));
          return;
        }

        // Legacy scenarios
        if (
          mockScenario.includes('error') ||
          mockScenario.includes('decline') ||
          mockScenario.includes('expired') ||
          mockScenario.includes('mismatch') ||
          mockScenario === 'max-attempts-flow'
        ) {
          const currentAttempts = (sessionState.attemptCount || 0) + 1;
          if (currentAttempts >= 3) {
            setSessionState((prev) => ({
              ...prev,
              screen: 'error',
              errorType: 'max-attempts',
              attemptCount: currentAttempts,
            }));
            handleSessionTermination({
              agentReturnUrl: initialSession?.agentReturnUrl,
              paymentId: initialSession?.paymentId ?? paymentId ?? sessionId,
              status: 'MAX_ATTEMPTS_REACHED',
              sessionId,
            });
            return;
          }

          let errorType:
            | 'retry-later'
            | 'dont-retry'
            | 'revalidate-input'
            | 'unexpected' = 'unexpected';
          if (
            mockScenario.includes('decline') ||
            mockScenario.includes('expired') ||
            mockScenario === 'max-attempts-flow'
          ) {
            errorType = 'dont-retry';
          } else if (mockScenario.includes('mismatch')) {
            errorType = 'revalidate-input';
          } else if (mockScenario.includes('retry')) {
            errorType = 'retry-later';
          }

          setSessionState((prev) => ({
            ...prev,
            screen: 'error',
            errorType,
            attemptCount: currentAttempts,
          }));
        } else {
          setSessionState((prev) => ({
            ...prev,
            screen: 'success',
            orderInfo: generateMockOrderInfo(amount, currency),
          }));
        }
      }, MOCK_DELAYS.PAYMENT_PROCESSING);
      return;
    }

    // Real BFF call (use apiClient if available)
    try {
      if (apiClient) {
        // Use authenticated API client (session context sent via x-session-id header)
        const saveCard = sessionState.cardEntry?.saveCard ?? false;
        const expiryDate = sessionState.cardEntry?.expiryDate ?? '';
        const [expiryMonthStr, expiryYearStr] = expiryDate.split('/');
        const expiryMonth = expiryMonthStr
          ? expiryMonthStr.padStart(2, '0')
          : undefined;
        const expiryYear = expiryYearStr
          ? expiryYearStr.length === 2
            ? String(2000 + parseInt(expiryYearStr, 10))
            : expiryYearStr
          : undefined;
        const cardholderName = sessionState.cardEntry?.cardholderName ?? '';
        const cardData =
          cardholderName &&
          expiryMonth !== undefined &&
          expiryYear !== undefined
            ? { cardholderName, expiryMonth, expiryYear }
            : undefined;
        const result = await apiClient.submitPayment(saveCard, cardData);

        setIsProcessing(false);
        setSessionState((prev) => ({
          ...prev,
          screen: 'success' as const,
          paymentStatus: 'completed' as const,
          orderInfo: {
            btPayId: result.paymentId || sessionId,
            merchantTransactionId:
              initialSession?.merchantTransactionId ||
              result.transactionId ||
              undefined,
            amount,
            currency,
            maskedCardNumber:
              sessionState.cardEntry?.cardNumber.value || '****',
            cardType: sessionState.cardEntry?.cardType || 'VISA',
            expiryDate: sessionState.cardEntry?.expiryDate || '',
            timestamp: new Date().toISOString(),
          },
        }));
      } else {
        throw new Error('API client not available — authentication incomplete');
      }
    } catch (err) {
      setIsProcessing(false);
      logger.error('Payment completion error', err);
      if (
        err instanceof ApiError &&
        err.errorCode?.toUpperCase() === 'MAX_ATTEMPTS_EXCEEDED'
      ) {
        setSessionState((prev) => ({
          ...prev,
          screen: 'error',
          errorType: 'max-attempts',
        }));
        onSessionDeclined?.(
          initialSession?.paymentId ?? paymentId ?? sessionId
        );
      } else if (
        err instanceof ApiError &&
        err.errorCode === 'PAYMENT_DECLINED'
      ) {
        const declined = err.details as PaymentDeclinedResponse | undefined;
        if (declined?.canRetry) {
          setSessionState((prev) => ({
            ...prev,
            screen: 'error',
            errorType: 'dont-retry',
          }));
        } else {
          setSessionState((prev) => ({
            ...prev,
            screen: 'error',
            errorType: 'max-attempts',
          }));
          onSessionDeclined?.(
            initialSession?.paymentId ?? paymentId ?? sessionId
          );
        }
      } else {
        setSessionState((prev) => ({
          ...prev,
          screen: 'error',
          errorType: 'unexpected',
        }));
        onError?.(err instanceof Error ? err : new Error('Payment failed'));
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleManualModeClick = async () => {
    if (!apiClient) {
      if (useMockData) {
        const mockCheckoutUrl = `${window.location.origin}${ROUTES.PAYMENT_SUCCESS}`;
        const mockWindow = window.open(mockCheckoutUrl, '_blank');
        if (checkoutWindowRef) checkoutWindowRef.current = mockWindow;
        setSessionState((prev) => ({
          ...prev,
          screen: 'waiting-manual-payment',
          wasManualPayment: true,
          popupBlockedUrl: mockCheckoutUrl,
        }));
      } else {
        onFallbackToManual?.(sessionId);
      }
      return;
    }

    try {
      // Block the SSE handler (FALLBACK_TO_MANUAL_MODE / VOICE_MANUAL_INITIATED)
      // from opening a second tab while this POST is in flight.
      if (checkoutOpenedRef) checkoutOpenedRef.current = true;

      // VOICE_MANUAL sessions already have a checkoutUrl (paymentLink) from the BFF —
      // calling to-manual would fail with INVALID_SESSION_STATE.
      const checkoutUrl =
        interactionType === 'VOICE_MANUAL' && paymentLink
          ? paymentLink
          : (await apiClient.initiateVoiceManual()).checkoutUrl;
      const newWindow = window.open(checkoutUrl, '_blank');
      if (checkoutWindowRef) checkoutWindowRef.current = newWindow;

      if (newWindow === null) {
        logger.warn('Popup blocked when opening checkout URL', { checkoutUrl });
        setSessionState((prev) => ({
          ...prev,
          screen: 'waiting-manual-payment',
          wasManualPayment: true,
          popupBlockedUrl: checkoutUrl,
          popupBlocked: true,
        }));
        return;
      }

      setSessionState((prev) => ({
        ...prev,
        screen: 'waiting-manual-payment',
        wasManualPayment: true,
        popupBlockedUrl: checkoutUrl,
        popupBlocked: false,
      }));
    } catch (err) {
      // Allow retry: reset the ref so the SSE guard doesn't permanently block
      if (checkoutOpenedRef) checkoutOpenedRef.current = false;
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to initiate manual payment';
      logger.error('Failed to initiate voice manual payment', err);
      setSessionState((prev) => ({
        ...prev,
        screen: 'error',
        errorType: 'unexpected',
        errorMessage,
      }));
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    }
  };

  // Re-opens the checkout page (popup-blocked recovery / accidentally-closed-tab link).
  // Routed through window.open (instead of a native <a target="_blank">) so a reference
  // to the tab is captured and can be closed later from handleCancel.
  const handleOpenCheckout = (url: string) => {
    const newWindow = window.open(url, '_blank');
    if (checkoutWindowRef) checkoutWindowRef.current = newWindow;
  };

  const handleFieldChange = (field: string, value: string) => {
    if (
      field !== 'cardholderName' &&
      field !== 'expiryDate' &&
      field !== 'saveCard'
    )
      return;
    if (
      field !== 'saveCard' &&
      sessionState.selectedPaymentMethod === 'saved-card'
    )
      return;

    setSessionState((prev) => ({
      ...prev,
      cardEntry: prev.cardEntry
        ? {
            ...prev.cardEntry,
            ...(field === 'cardholderName' && { cardholderName: value }),
            ...(field === 'expiryDate' && { expiryDate: value }),
            ...(field === 'saveCard' && { saveCard: value === 'true' }),
          }
        : prev.cardEntry,
    }));
  };

  const handleTryAgain = () => {
    const errorType = sessionState.errorType;

    // max-attempts is handled by handleCancel (wired as the ErrorScreen
    // onAction in the component), so the button never invokes handleTryAgain.
    // Guard defensively so a direct call can't fall through to the generic
    // card-entry reset below.
    if (errorType === 'max-attempts') {
      return;
    }

    if (errorType === 'card-has-mandates') {
      setSessionState((prev) => ({
        ...prev,
        screen: 'payment-method-selection',
        errorType: undefined,
      }));
      return;
    }

    // In mock mode, restart the full flow so DTMF simulation runs again
    if (useMockData) {
      refetch();
      return;
    }

    // A saved-card payment that failed must return to the selection screen so
    // the agent can re-pick a saved card or new card — not drop straight into
    // the new-card/DTMF entry. Only when saved cards remain; otherwise fall
    // through to card-entry below (PAS-32325).
    if (
      sessionState.selectedPaymentMethod === 'saved-card' &&
      (sessionState.savedCards?.length ?? 0) > 0
    ) {
      setSessionState((prev) => ({
        ...prev,
        screen: 'payment-method-selection',
        errorType: undefined,
        selectedPaymentMethod: undefined,
        selectedSavedCardId: undefined,
      }));
      return;
    }

    // Clear all card fields on retry for terminal/unexpected errors so the agent
    // starts from an empty form. dont-retry (bank decline) and unexpected (5xx /
    // timeout, e.g. Fraud Check rejection surfaced as an unexpected error) must
    // not pre-fill the previous card details (PAS-32663).
    if (errorType === 'dont-retry' || errorType === 'unexpected') {
      setSessionState((prev) => ({
        ...prev,
        screen: 'card-entry',
        errorType: undefined,
        selectedPaymentMethod: 'new-card',
        selectedSavedCardId: undefined,
        cardEntry: {
          cardholderName: '',
          cardNumber: createEmptyCardNumber(),
          cvv: createEmptyCVV(),
          expiryDate: '',
          saveCard: shouldSaveCard === 'ALWAYS',
        },
      }));
    } else {
      setSessionState((prev) => ({
        ...prev,
        screen: 'card-entry',
        errorType: undefined,
        selectedPaymentMethod: 'new-card',
        selectedSavedCardId: undefined,
        cardEntry: prev.cardEntry
          ? { ...prev.cardEntry }
          : {
              cardholderName: '',
              cardNumber: createEmptyCardNumber(),
              cvv: createEmptyCVV(),
              expiryDate: '',
              saveCard: shouldSaveCard === 'ALWAYS',
            },
      }));
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    const agentReturnUrl = initialSession?.agentReturnUrl;
    const resolvedPaymentId =
      initialSession?.paymentId ?? paymentId ?? sessionId;

    logger.info('Cancel button clicked', {
      paymentId: resolvedPaymentId,
      sessionId,
      agentReturnUrl,
    });

    // Best-effort: close the checkout tab opened for this payment (SSE-driven,
    // manual mode click, or the popup-blocked recovery link). Wrapped because
    // window.close() can throw on a window not opened by this script (e.g. the
    // legacy noopener case) or one already closed.
    try {
      checkoutWindowRef?.current?.close();
    } catch (err) {
      logger.warn('Failed to close checkout window on cancel', {
        sessionId,
        error: err instanceof Error ? err.message : String(err),
      });
    }
    if (checkoutWindowRef) checkoutWindowRef.current = null;

    if (!useMockData && apiClient) {
      try {
        await apiClient.cancelPayment();
        logger.info('Cancel API call succeeded', { sessionId });
      } catch (err) {
        logger.warn('Cancel API call failed, proceeding with UI cancel', {
          sessionId,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    if (agentReturnUrl && agentReturnUrl.trim() !== '') {
      logger.info('Cancel action determined: redirect', { agentReturnUrl });
      const cancelUrl = appendQueryParams(agentReturnUrl, {
        paymentId: resolvedPaymentId,
      });
      logger.info('Payment cancelled, redirecting to', { cancelUrl });
      window.location.href = cancelUrl;
    } else {
      logger.info('Cancel action determined: postMessage', { agentReturnUrl });
      window.parent.postMessage(
        {
          eventName: 'payment-cancelled',
          type: 'payment.cancelled',
          payload: {
            paymentId: resolvedPaymentId,
            sessionId,
            status: 'CANCELLED',
            timestamp: new Date().toISOString(),
          },
        },
        '*'
      );
      logger.info('Payment cancelled via postMessage', {
        paymentId: resolvedPaymentId,
        sessionId,
      });
    }
  };

  return {
    isProcessing,
    isCancelling,
    handlePaymentMethodSelect,
    handleDeleteCardClick,
    handleDeleteCardConfirm,
    handleDeleteCardCancel,
    handleResetSensitiveData,
    handleCompletePayment,
    handleManualModeClick,
    handleOpenCheckout,
    handleFieldChange,
    handleTryAgain,
    handleCancel,
  };
};
